import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const ConfirmContext = createContext(null);

/**
 * Promise-based confirmation popups that replace the native window.confirm().
 * Usage:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title, message, confirmText, danger }))) return;
 * A bare string is also accepted: await confirm('Are you sure?')
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolver = useRef(null);

  const close = useCallback((result) => {
    setDialog(null);
    if (resolver.current) {
      resolver.current(result);
      resolver.current = null;
    }
  }, []);

  const confirm = useCallback((opts) => {
    const o = typeof opts === 'string' ? { message: opts } : (opts || {});
    setDialog({
      title: o.title || 'Please confirm',
      message: o.message || '',
      confirmText: o.confirmText || 'Confirm',
      cancelText: o.cancelText || 'Cancel',
      danger: !!o.danger,
    });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  // Escape always cancels; the focused button handles Enter/Space.
  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div className="modal-overlay" onMouseDown={() => close(false)}>
          <div
            className="modal confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-label={dialog.title}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 className="confirm-title">{dialog.title}</h3>
            <p className="confirm-message">{dialog.message}</p>
            <div className="modal-actions">
              {/* On destructive prompts focus Cancel so a stray Enter is safe. */}
              <button
                className="btn btn-outline"
                autoFocus={dialog.danger}
                onClick={() => close(false)}
              >
                {dialog.cancelText}
              </button>
              <button
                className={`btn ${dialog.danger ? 'btn-danger' : 'btn-primary'}`}
                autoFocus={!dialog.danger}
                onClick={() => close(true)}
              >
                {dialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
